import { supabase } from './supabase';

export interface UserCredits {
  credits: number;
  total_credits: number;
}

export async function getUserCredits(): Promise<UserCredits | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('users')
      .select('credits, total_credits')
      .eq('id', user.id)
      .single();

    if (error) throw error;
    return data || null;
  } catch (error) {
    console.error('Error getting user credits:', error);
    return null;
  }
}

export async function checkUserHasEnoughCredits(taskId: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    // Get task details
    const { data: task } = await supabase
      .from('scheduled_tasks')
      .select('webhook_url')
      .eq('id', taskId)
      .single();

    if (!task) return false;

    // Calculate required credits
    const requiredCredits = task.webhook_url === import.meta.env.VITE_DEFAULT_WEBHOOK ? 50 : 25;

    // Get user credits
    const { data: userCredits } = await supabase
      .from('users')
      .select('credits')
      .eq('id', user.id)
      .single();

    if (!userCredits) return false;

    return userCredits.credits >= requiredCredits;
  } catch (error) {
    console.error('Error checking credits:', error);
    return false;
  }
}

export async function chargeUserCredits(taskId: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    // Get task details
    const { data: task } = await supabase
      .from('scheduled_tasks')
      .select('webhook_url, content')
      .eq('id', taskId)
      .single();

    if (!task) return false;

    // Calculate required credits
    const requiredCredits = task.webhook_url === import.meta.env.VITE_DEFAULT_WEBHOOK ? 50 : 25;

    // Get current credits
    const { data: userCredits } = await supabase
      .from('users')
      .select('credits')
      .eq('id', user.id)
      .single();

    if (!userCredits || userCredits.credits < requiredCredits) {
      return false;
    }

    // Update credits
    const { error: updateError } = await supabase
      .from('users')
      .update({ credits: userCredits.credits - requiredCredits })
      .eq('id', user.id);

    if (updateError) throw updateError;

    return true;
  } catch (error) {
    console.error('Error charging credits:', error);
    return false;
  }
}